import template from "./sw-toc.mjs";
import { render } from "lit-html";
import { HeartBank } from "../../../managers/HeartBank";
import { Prompts } from "../../../managers/Prompts";

import { Drawer } from "flowbite";
import type { DrawerOptions, DrawerInterface } from "flowbite";
import type { InstanceOptions } from "flowbite";

import { LANGUAGE } from "../../global";
import Strings from "../../../models/Strings.json";
const strings: any = Strings;

export class SwToc extends HTMLElement {
    private heartbank = new HeartBank();
    private prompts = new Prompts();
    private drawer: DrawerInterface;

    private toc: HTMLElement;
    private prompt: HTMLTextAreaElement;
    private submit: HTMLButtonElement;
    private global: HTMLButtonElement;
    private copy: HTMLButtonElement;
    private paste: HTMLButtonElement;
    private clear: HTMLButtonElement;
    private history: HTMLUListElement;

    private button4: HTMLButtonElement;
    private button5: HTMLButtonElement;
    private basket4 = false;
    private basket5 = false;

    constructor() {
        super();
        render(template(null), this);

        this.toc = this.querySelector("#sw-toc")!;
        this.prompt = this.querySelector("#sw-toc-prompt")!;
        this.global = this.querySelector("#sw-toc-global")!;
        this.submit = this.querySelector("#sw-toc-submit")!;
        this.copy = this.querySelector("#sw-toc-copy")!;
        this.paste = this.querySelector("#sw-toc-paste")!;
        this.clear = this.querySelector("#sw-toc-clear")!;
        this.history = this.querySelector("#sw-toc-history")!;

        this.button4 = this.querySelector("#sw-toc-commentaries-khmer-btn")!;
        this.button5 = this.querySelector("#sw-toc-commentaries-pali-btn")!;

        // options with default values
        const options: DrawerOptions = {
            placement: "right",
            backdrop: true,
            bodyScrolling: false,
            edge: false,
            edgeOffset: "",
            backdropClasses:
                "bg-gray-900/50 dark:bg-gray-900/80 fixed inset-0 z-30",
            onHide: () => {
                //console.log("drawer is hidden");
            },
            onShow: () => {
                //console.log("drawer is shown");
            },
            onToggle: () => {
                //console.log("drawer has been toggled");
            }
        };

        // instance options object
        const instanceOptions: InstanceOptions = {
            id: "sw-toc",
            override: true
        };

        /*
         * $targetEl (required)
         * options (optional)
         * instanceOptions (optional)
         */
        this.drawer = new Drawer(this.toc, options, instanceOptions);
    }

    private isGlobal(): boolean {
        return this.heartbank.user
            ? localStorage.getItem("global") === "1"
            : localStorage.getItem("global") !== "0";
    }

    private renderGlobal() {
        const svg = this.global.querySelector("svg") as SVGElement;
        svg.setAttribute("fill", this.isGlobal() ? "skyblue" : "currentColor");

        // doesn't work
        // this.global.classList.toggle("text-gray-500", !isGlobal);
        // this.global.classList.toggle("dark:text-gray-400", !isGlobal);
        // this.global.classList.toggle("text-orange-500", isGlobal);
        // this.global.classList.toggle("dark:text-orange-400", isGlobal);
    }

    connectedCallback() {
        this.render();
        this.renderGlobal();

        this.prompt.addEventListener("keyup", () =>
            this.render(this.prompt.value.trim())
        );

        this.submit.addEventListener("click", async () => {
            const prompt = this.prompt.value.trim();
            if (prompt) {
                this.submit.disabled = true;
                this.copy.click(); // order is important

                // need to allow popup
                setTimeout(
                    () => window.open("https://gemini.google.com", "_blank"),
                    1500
                );

                await this.prompts.add({
                    did: this.heartbank.getDeviceID(),
                    uid: this.heartbank.user?.uid || "",
                    prompt,
                    count: 1
                });
                this.prompt.value = "";
                await this.render();

                this.submit.disabled = false;
                this.heartbank.alertAdmin(`toc/submit/`, prompt);
            }
        });

        this.global.addEventListener("click", async () => {
            this.global.disabled = true;

            localStorage.setItem("global", this.isGlobal() ? "0" : "1");
            this.renderGlobal();
            await this.render();

            this.heartbank.alertAdmin(
                `toc/global/`,
                this.isGlobal().toString()
            );
            this.global.disabled = false;
        });

        this.copy.addEventListener("click", async () => {
            const prompt = this.prompt.value.trim();
            if (prompt) {
                await navigator.clipboard.writeText(prompt);
                this.dispatchEvent(
                    new CustomEvent<{ info: string }>("sw-banner", {
                        bubbles: true,
                        detail: {
                            info: strings.copied3[LANGUAGE]
                        }
                    })
                );
                this.heartbank.alertAdmin(`toc/copy/`, prompt);
            }
        });

        this.paste.addEventListener("click", async () => {
            const prompt = this.prompt.value.trim();
            if (!prompt || confirm(strings.paste[LANGUAGE])) {
                this.prompt.value = await navigator.clipboard.readText();
                //this.render();
                this.prompt.focus();
                this.heartbank.alertAdmin(`toc/paste/`, this.prompt.value);
            }
        });

        this.clear.addEventListener("click", async () => {
            this.heartbank.alertAdmin(`toc/clear/`, this.prompt.value);
            this.prompt.value = "";
            this.render();
            this.prompt.focus();
        });

        this.button4.addEventListener("click", () => {
            const svg = this.button4.lastElementChild as SVGElement;
            if (this.basket4) {
                svg.classList.remove("rotate-180");
                this.basket4 = false;
            } else {
                svg.classList.add("rotate-180");
                this.basket4 = true;
            }
        });
        this.button5.addEventListener("click", () => {
            const svg = this.button5.lastElementChild as SVGElement;
            if (this.basket5) {
                svg.classList.remove("rotate-180");
                this.basket5 = false;
            } else {
                svg.classList.add("rotate-180");
                this.basket5 = true;
            }
        });
    }

    private async render(prompt = "") {
        this.history.innerHTML = "";
        const prompts = await this.prompts.get(
            this.heartbank.getDeviceID(),
            this.heartbank.user?.uid || "",
            prompt,
            this.isGlobal()
        );

        for (const prompt of prompts) {
            const li = document.createElement("li");
            li.className = "flex justify-between";

            const span = document.createElement("span");
            span.className = "mr-2";
            span.textContent = "🕖";

            const p = document.createElement("button");
            p.type = "button";
            p.title = prompt.prompt;
            p.append(
                span,
                this.truncate(prompt.prompt, LANGUAGE === "khmer" ? 35 : 35)
            );
            p.onclick = () => {
                this.prompt.value = prompt.prompt;
                //this.render();
            };

            const button = document.createElement("button");
            button.type = "button";
            button.textContent =
                prompt.did === "" ? (prompt.count || 1).toString() : "🗑️";
            if (this.heartbank.isAdmin() || prompt.did !== "")
                button.onclick = async () => {
                    button.disabled = true;
                    li.classList.add("opacity-50");
                    if (confirm(strings.trash[LANGUAGE])) {
                        await this.prompts.remove(prompt.id!);
                        //this.render();
                        li.remove();
                    } else {
                        button.disabled = false;
                        li.classList.remove("opacity-50");
                    }
                };

            li.append(p, button);
            this.history.appendChild(li);
        }
    }

    show(lang: string, book: number, page: number) {
        this.drawer.show();
        this.prompt.focus();
    }

    hide() {
        this.drawer.hide();
    }

    private truncate(text: string, maxLength: number): string {
        if (text.length <= maxLength) return text;
        return text.slice(0, maxLength).trimEnd() + "...";
    }
}
